/**
 * What a list says when it has nothing in it.
 *
 * A control rather than a string on the page, for one reason: it is `visible`-toggled beside the
 * list it belongs to, so "no customers yet" and a list of customers can never be on screen at the
 * same time, and neither can be absent. The OpenSilver pages bound an empty-state TextBlock's
 * `Visibility` to one property and the `ItemsControl` to another, which is two things that have to
 * agree - and on the software page they briefly did not.
 *
 * It is a card, not a bare line, because an empty list on a page of cards otherwise reads as a
 * section that failed to load rather than one with nothing to show.
 */
import { Card } from './Card.js';
import { card, text } from './layout.js';
import type { CrmLabel } from './Text.js';

export class EmptyState {
  readonly panel: Card;
  readonly #label: CrmLabel;

  constructor(message: string) {
    this.panel = card({ tint: 'alt', visible: false });
    this.#label = text(message, { role: 'subtle' });
    this.panel.controls.add(this.#label);
  }

  /** Shows the message only when `count` is zero. The one call every list makes after it renders. */
  update(count: number, message?: string): void {
    if (message !== undefined) this.#label.text = message;
    this.panel.visible = count === 0;
  }

  set message(value: string) {
    this.#label.text = value;
  }
}

/** The one-liner: an empty state and the list it guards, ready to add in order. */
export function emptyState(message: string): EmptyState {
  return new EmptyState(message);
}
